import React, { useState, useEffect } from 'react';
import {
  RiSparklingLine as SparklesIcon,
  RiFileTextLine as FileIcon,
  RiCloseLine as CloseIcon,
} from 'react-icons/ri';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import PetitionService, { GeneratePetitionOptions, ExhibitList } from '@/api/petitions';
import { knowledgeBaseDocumentApi } from '@/api/knowledge-base';
import { KnowledgeBaseContentType } from '@/types/knowledge-base';

interface PetitionGenerationFormProps {
  caseId: string;
  visaType?: string;
  onCancel?: () => void;
  onSuccess?: (petitionId: string) => void;
  onError?: (error: string) => void;
}

const PetitionGenerationForm: React.FC<PetitionGenerationFormProps> = ({
  caseId,
  visaType,
  onCancel,
  onSuccess,
  onError,
}) => {
  const [generating, setGenerating] = useState(false);
  // Use the case's visaType if available, otherwise leave empty (backend will handle)
  const [petitionType, setPetitionType] = useState<string>(visaType || '');
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [exhibitLists, setExhibitLists] = useState<ExhibitList[]>([]);
  const [selectedExhibitListId, setSelectedExhibitListId] = useState<string>('');
  const [includeExhibitIndex, setIncludeExhibitIndex] = useState(true);
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
  const [progressStage, setProgressStage] = useState<string>('');
  const [progressPercent, setProgressPercent] = useState<number>(0);

  // Auto-populate visa type from case when available
  useEffect(() => {
    if (visaType) {
      setPetitionType(visaType);
    }
  }, [visaType]);

  useEffect(() => {
    loadExhibitLists();

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [caseId]);

  const loadExhibitLists = async () => {
    try {
      const response = await PetitionService.getExhibitListsForCase(caseId);
      if (response.success && response.data) {
        setExhibitLists(response.data);
        // Auto-select the most recent exhibit list
        if (response.data.length > 0) {
          setSelectedExhibitListId(response.data[0].id);
        }
      }
    } catch (err: any) {
      console.error('Failed to load exhibit lists:', err);
      // Don't show error to user, exhibit list is optional
    }
  };

  const handleGeneratePetition = async () => {
    try {
      setGenerating(true);
      setProgressStage('Starting petition generation...');
      setProgressPercent(0);

      // Upload template file to knowledge base if provided
      let templateId: string | undefined;
      if (templateFile) {
        try {
          setUploadingTemplate(true);
          console.log('[PetitionGenerationForm] Uploading template to knowledge base:', templateFile.name);

          const uploadResult = await knowledgeBaseDocumentApi.uploadFile(templateFile, {
            title: `Petition Template - ${templateFile.name}`,
            contentType: KnowledgeBaseContentType.TEMPLATE,
            tags: ['petition-template', petitionType],
            visaTypes: [petitionType],
          });

          templateId = uploadResult.documentId;
          console.log('[PetitionGenerationForm] Template uploaded successfully:', templateId);
        } catch (uploadError: any) {
          console.error('[PetitionGenerationForm] Failed to upload template:', uploadError);
          onError?.(`Failed to upload template: ${uploadError.message || 'Unknown error'}`);
          setGenerating(false);
          setUploadingTemplate(false);
          return;
        } finally {
          setUploadingTemplate(false);
        }
      }

      const options: GeneratePetitionOptions = {
        petitionType,
        customPrompt: customPrompt.trim() || undefined,
        exhibitListId: selectedExhibitListId || undefined,
        includeExhibitIndex,
        templateId,
      };

      console.log('[PetitionGenerationForm] Generating petition with options:', options);
      const response = await PetitionService.generatePetition(caseId, options);

      if (response.success && response.data) {
        const petitionId = response.data.petitionId;

        // Start polling for status
        const interval = setInterval(async () => {
          try {
            console.log('[PetitionGenerationForm] Polling petition status:', petitionId);
            const statusResponse = await PetitionService.getPetitionStatus(petitionId);
            console.log('[PetitionGenerationForm] Status response:', statusResponse);

            if (statusResponse.success && statusResponse.data) {
              const { status, content } = statusResponse.data;
              console.log('[PetitionGenerationForm] Current petition status:', status, 'content:', content);

              // Update progress info from content if available
              if (content && typeof content === 'object') {
                const contentObj = content as { stage?: string; progress?: number };
                if (contentObj.stage) {
                  setProgressStage(contentObj.stage);
                }
                if (typeof contentObj.progress === 'number') {
                  setProgressPercent(contentObj.progress);
                }
              }

              // Check for completed states: 'completed', 'approved', 'done', 'generated'
              const isCompleted = status === 'completed' || status === 'approved' || status === 'done' || status === 'generated';
              const isFailed = status === 'failed' || status === 'error' || status === 'rejected';

              if (isCompleted || isFailed) {
                console.log('[PetitionGenerationForm] Petition generation finished. isCompleted:', isCompleted, 'isFailed:', isFailed);
                clearInterval(interval);
                setPollingInterval(null);
                setGenerating(false);

                if (isCompleted) {
                  console.log('[PetitionGenerationForm] Calling onSuccess with petition ID:', petitionId);
                  onSuccess?.(petitionId);
                } else {
                  console.log('[PetitionGenerationForm] Calling onError - generation failed');
                  onError?.('Petition generation failed. Please try again.');
                }
              }
            } else {
              console.warn('[PetitionGenerationForm] Invalid status response:', statusResponse);
            }
          } catch (pollError: any) {
            console.error('[PetitionGenerationForm] Error polling status:', pollError);
            // Don't stop polling on transient errors, just log them
          }
        }, 5000); // Poll every 5 seconds

        setPollingInterval(interval);
      } else {
        throw new Error(response.message || 'Failed to generate petition');
      }
    } catch (err: any) {
      console.error('Failed to generate petition:', err);
      onError?.(err.message || 'Failed to generate petition');
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Visa Type Info */}
      <div className="border border-neutral-200 rounded-lg px-4 py-3 text-left">
        <label className="block font-medium text-neutral-700 mb-2">
          Petition Type
        </label>
        {visaType ? (
          <div className="flex items-center space-x-2">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
              {visaType}
            </span>
            <span className="text-sm text-neutral-500">
              (from case visa type)
            </span>
          </div>
        ) : (
          <div className="flex items-center space-x-2 text-warning-600">
            <span className="text-sm">
              ⚠️ No visa type set for this case. Please set the case visa type before generating a petition.
            </span>
          </div>
        )}
      </div>

      {/* Exhibit List Selection */}
      <div className="border border-neutral-200 rounded-lg px-4 py-3 text-left">
        <label className="block font-medium text-neutral-700 mb-2">
          Exhibit List <span className="text-neutral-500 font-normal">(Optional)</span>
        </label>
        {exhibitLists.length > 0 ? (
          <select
            value={selectedExhibitListId}
            onChange={(e) => setSelectedExhibitListId(e.target.value)}
            disabled={generating}
            className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm text-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Generate new exhibit list automatically</option>
            {exhibitLists.map((list) => (
              <option key={list.id} value={list.id}>
                {list.title} - {list.totalExhibits} exhibits ({new Date(list.createdAt).toLocaleDateString()})
              </option>
            ))}
          </select>
        ) : (
          <p className="text-sm text-neutral-500">
            No exhibit lists found. A new one will be generated automatically.
          </p>
        )}
      </div>

      {/* Additional Options */}
      <div className="border border-neutral-200 rounded-lg px-4 py-3 text-left">
        <h4 className="font-medium text-neutral-700 mb-3">Petition Options</h4>
        <div className="space-y-3">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeExhibitIndex}
              onChange={(e) => setIncludeExhibitIndex(e.target.checked)}
              disabled={generating}
              className="w-4 h-4 text-primary-600 rounded border-neutral-300 focus:ring-primary-500"
            />
            <span className="text-sm text-neutral-700">
              <strong>Include Exhibit Index</strong> - Add a "List of Exhibits" section in the petition
            </span>
          </label>
          <p className="text-xs text-neutral-500 ml-6">
            Uncheck if you reference exhibits directly in the cover letter and don't need a separate exhibit index section.
          </p>
        </div>
      </div>

      {/* Custom Instructions */}
      <div className="border border-neutral-200 rounded-lg px-4 py-3 text-left">
        <label className="block font-medium text-neutral-700 mb-2">
          Custom Instructions <span className="text-neutral-500 font-normal">(Optional)</span>
        </label>
        <textarea
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e.target.value)}
          disabled={generating}
          placeholder="Add specific instructions for the AI (e.g., 'Emphasize research publications', 'Focus on leadership roles', 'Highlight international recognition', etc.)"
          className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
          rows={3}
        />
        <p className="mt-1 text-xs text-neutral-500">
          Provide additional context or emphasis areas to customize the petition content.
        </p>
      </div>

      {/* Template Upload */}
      <div className="border border-neutral-200 rounded-lg px-4 py-3 text-left">
        <label className="block font-medium text-neutral-700 mb-2">
          Petition Template <span className="text-neutral-500 font-normal">(Optional)</span>
        </label>
        <div className="space-y-2">
          {!templateFile ? (
            <div>
              <input
                type="file"
                id="template-upload"
                accept=".pdf,.docx,.txt,.md"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setTemplateFile(file);
                  }
                }}
                disabled={generating}
                className="hidden"
              />
              <label
                htmlFor="template-upload"
                className={`flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed border-neutral-300 rounded-md cursor-pointer hover:border-primary-400 hover:bg-primary-50 transition-colors ${
                  generating ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <FileIcon className="w-5 h-5 text-neutral-500" />
                <span className="text-sm text-neutral-600">Click to upload template (PDF, DOCX, TXT, MD)</span>
              </label>
            </div>
          ) : (
            <div className="flex items-center justify-between px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-md">
              <div className="flex items-center space-x-2">
                <FileIcon className="w-4 h-4 text-primary-600" />
                <div>
                  <p className="text-sm font-medium text-neutral-700">{templateFile.name}</p>
                  <p className="text-xs text-neutral-500">
                    {(templateFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              <button
                onClick={() => setTemplateFile(null)}
                disabled={generating}
                className="text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
          )}
          <p className="text-xs text-neutral-500">
            Upload a template petition to guide the AI in generating sections with similar structure and style.
            The template will be parsed and used as an example for the AI to understand your preferred format.
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-3">
        {onCancel && (
          <Button
            variant="ghost"
            onClick={onCancel}
            disabled={generating}
          >
            Cancel
          </Button>
        )}
        <div className="flex flex-col items-end gap-1">
          <Button
            onClick={handleGeneratePetition}
            disabled={generating}
            className="flex items-center space-x-2"
          >
            {uploadingTemplate ? (
              <>
                <LoadingSpinner size="sm" />
                <span>Uploading Template...</span>
              </>
            ) : generating ? (
              <>
                <LoadingSpinner size="sm" />
                <span>Generating... {progressPercent > 0 && `${progressPercent}%`}</span>
              </>
            ) : (
              <>
                <SparklesIcon className="h-4 w-4" />
                <span>Generate Petition</span>
              </>
            )}
          </Button>
          {generating && (
            <div className="flex flex-col items-end gap-1 mt-1 w-64">
              {/* Progress Bar */}
              <div className="w-full bg-neutral-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-primary-500 h-2 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${Math.max(progressPercent, 2)}%` }}
                />
              </div>
              {/* Stage Message */}
              <p className="text-xs text-neutral-600 text-right truncate w-full" title={progressStage}>
                {progressStage || 'Initializing...'}
              </p>
              <p className="text-xs text-neutral-400">
                May take 5-10 minutes
              </p>
            </div>
          )}
          {!generating && (
            <p className="text-xs text-neutral-400">
              Generation time: 5-10 minutes
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PetitionGenerationForm;
